/**
 * Table Suggestion Hook
 *
 * Analyzes an uploaded file and suggests the best matching table
 * based on field name similarity.
 *
 * @module features/versions/hooks/useSuggestTable
 */

import { useMutation } from '@tanstack/react-query';

import { useRpc } from '@/lib/rpc';

export interface TableSuggestion {
  tableId: string;
  tableName: string;
  matchScore: number;
}

export interface SuggestTableResult {
  suggestion: TableSuggestion | null;
  reason?: string;
}

/**
 * Hook to suggest a table based on file field names
 *
 * Sends the file to the API which extracts field names and
 * compares them against existing tables in the workspace.
 *
 * @returns Mutation for suggesting a table
 */
export function useSuggestTable() {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async (file: File): Promise<SuggestTableResult> => {
      const formData = new FormData();
      formData.append('file', file);

      const res = await rpc.api.private.uploads['suggest-table'].$post({
        form: { file },
      });

      if (!res.ok) {
        throw new Error('Failed to get table suggestion');
      }

      return res.json() as Promise<SuggestTableResult>;
    },
  });
}
